/**
 * 체크아웃 결제 데이터 생성 유틸리티
 *
 * @see #135 - CheckoutPage handleProceedToPayment 함수 복잡도 개선
 * @see #217/#218 β-2b - combination 기반 재구성
 */

import type { CartItem } from '@/types';
import type { Order, Payment, PaymentMethodCombination } from '@/lib/api/types';
import type { PaymentMethodType } from '@/config/paymentMethods';
import type { PaymentData } from '@/contexts/CheckoutContext';
import { asNet, asTax, asTip, makeReceiptAmounts, type NetAmount, type TaxAmount, type TipAmount } from '@/types/money';

/**
 * CheckoutContext용 결제 데이터 인터페이스
 *
 * PaymentData 인터페이스와 호환을 위해 index signature 포함
 */
export interface CheckoutPaymentData extends PaymentData {
  orderId: string;
  orderDbId: number;
  paymentId: number;
  /** 비회원 주문 조회 코드 (로그인 사용자는 undefined) */
  guestPassword?: string;
  timestamp: Date;
  restaurant: string;
  items: CartItem[];
  /** NET 소계 (세금 제외) — 영수증 Subtotal 행에 사용 */
  netSubtotal: number;
  /** GROSS 합계 (IVA 포함) — #299 에서 `subtotal` 에서 변경 */
  grossSubtotal: number;
  tax: number;
  total: number;
  /**
   * 결제 수단 구분 (영수증/주문 내역 표시용).
   * β-2b: combination.method 기반 도출 ('card' | 'cash').
   * 'points' 는 포인트 전액 결제 특수 케이스 (amount=0 + points>0).
   */
  paymentMethod: PaymentMethodType;
  /**
   * @deprecated #687: 항상 false. 클라이언트 모의 결제 상태머신 진입 플래그였으나
   * 거짓 '결제 완료' 화면의 root cause 로 확인되어 무력화됨 (타입 호환용으로만 유지).
   */
  showPaymentProcessing: boolean;
}

/**
 * 주문/결제 응답을 CheckoutContext용 데이터로 변환 (β-2b).
 *
 * @param order 주문 응답
 * @param payment 결제 응답
 * @param guestPassword 비회원 주문 조회 코드 (로그인 사용자는 undefined)
 * @param cartItems 장바구니 아이템
 * @param orderCalculation 주문 금액 계산 결과
 * @param combination 선택된 결제 조합 (β-2b)
 * @returns CheckoutContext용 결제 데이터
 */
/**
 * 주문 금액 계산 입력 (#336 B): 호출처가 직접 GROSS / TOTAL 산술하지 못하도록
 * NET + TAX (+ optional TIP / discount) 만 받음. makeReceiptAmounts (#301 Phase 3)
 * 가 GROSS = NET + TAX, TOTAL = GROSS + TIP - DISCOUNT 강제.
 */
export interface OrderCalculationInput {
  net: NetAmount | number;
  tax: TaxAmount | number;
  tip?: TipAmount | number;
  discount?: number;
}

export function createPaymentData(
  order: Order,
  payment: Payment,
  guestPassword: string | undefined,
  cartItems: CartItem[],
  orderCalculation: OrderCalculationInput,
  combination: PaymentMethodCombination
): CheckoutPaymentData {
  const firstItem = cartItems[0];

  // 포인트 전액 결제 (amount=0 + points>0) 는 'points' 표시, 그 외는 combination.method 사용.
  const isPointsOnly = (payment.points_amount ?? 0) > 0 && parseFloat(payment.amount) === 0;
  const displayMethod: PaymentMethodType = isPointsOnly ? 'points' : combination.method;

  // #336 B: factory 통과 → GROSS = NET + TAX 불변식 캡슐화. 호출처가 미리 GROSS
  // 를 산술해 넘기더라도 본 함수에서 net + tax 로부터 재산출 (drift 차단).
  const amounts = makeReceiptAmounts({
    net: asNet(orderCalculation.net),
    tax: asTax(orderCalculation.tax),
    tip: orderCalculation.tip != null ? asTip(orderCalculation.tip) : undefined,
    discount: orderCalculation.discount,
  });

  return {
    orderId: order.order_number || `ORDER-${order.id}`,
    orderDbId: order.id,
    paymentId: payment.id,
    guestPassword,
    timestamp: new Date(),
    restaurant: firstItem?.restaurantName || '',
    items: cartItems,
    netSubtotal: amounts.netSubtotal,
    grossSubtotal: amounts.grossSubtotal,
    tax: amounts.tax,
    total: amounts.total,
    paymentMethod: displayMethod,
    // #687 fail-closed: 게이트웨이 카드 결제의 완료 판정 권위는 3DS challenge 완주 후
    // 서버 콜백(/payment/result outcome)뿐이다. 이 플래그가 true 면 CheckoutContext 가
    // 클라이언트 모의 결제 상태머신(paymentFlow 'processing' → 'success')을 무장시켜,
    // 3DS 카드 입력 화면에서 브라우저 뒤로가기 복귀 시 '결제중' 모달 잔존 →
    // 모의 시뮬레이션이 거짓 '결제 완료'(ReceiptPage) 를 렌더했다 — 항상 false 로 박제.
    showPaymentProcessing: false,
    // #285 stale 방지: 주문 응답의 세율(퍼센트)·통화를 비율로 변환해 스냅샷 저장
    taxRate: order.tax_rate != null ? order.tax_rate / 100 : undefined,
    currency: order.currency,
    // #647 C1: combination timing/code 스냅샷 — 로컬 주문 snapshot 이 미결제 후불
    // (latestPayment) 정보를 보존해 주문내역의 후불 결제 진입 게이트가 동작하도록.
    paymentTiming: combination.timing,
    combinationCode: combination.code,
  };
}
